import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom';
import { RootLayout } from '@/components/layout/RootLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoginForm } from '@/components/auth/LoginForm';
import { ForgotPasswordForm } from '@/components/auth/ForgotPasswordForm';
import { ResetPasswordPage } from '@/components/auth/ResetPasswordPage';
import { AuthCallbackPage } from '@/components/auth/AuthCallbackPage';
import { NoAccountPage } from '@/components/auth/NoAccountPage';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { NotFound } from '@/components/layout/NotFound';
import { AdminConsoleLayout } from '@/components/admin/AdminConsoleLayout';

const ICropDashboard = lazy(() =>
  import('@/components/admin/iCropDashboard').then((m) => ({ default: m.ICropDashboard })),
);
const WorkspacesList = lazy(() =>
  import('@/components/admin/WorkspacesList').then((m) => ({ default: m.WorkspacesList })),
);
const WorkspaceDetail = lazy(() =>
  import('@/components/admin/WorkspaceDetail').then((m) => ({ default: m.WorkspaceDetail })),
);
const UsersList = lazy(() =>
  import('@/components/admin/UsersList').then((m) => ({ default: m.UsersList })),
);
const UserDetail = lazy(() =>
  import('@/components/admin/UserDetail').then((m) => ({ default: m.UserDetail })),
);
const PlansList = lazy(() =>
  import('@/components/admin/PlansList').then((m) => ({ default: m.PlansList })),
);
const PlanForm = lazy(() =>
  import('@/components/admin/PlanForm').then((m) => ({ default: m.PlanForm })),
);
const GlobalDoctypesManager = lazy(() =>
  import('@/components/admin/GlobalDoctypesManager').then((m) => ({
    default: m.GlobalDoctypesManager,
  })),
);
const GlobalOrgTypesManager = lazy(() =>
  import('@/components/admin/GlobalOrgTypesManager').then((m) => ({
    default: m.GlobalOrgTypesManager,
  })),
);
const GlobalProductsManager = lazy(() =>
  import('@/components/admin/GlobalProductsManager').then((m) => ({
    default: m.GlobalProductsManager,
  })),
);
const AuditLogViewer = lazy(() =>
  import('@/components/admin/AuditLogViewer').then((m) => ({
    default: m.AuditLogViewer,
  })),
);
const PublicUploadPage = lazy(() =>
  import('@/components/upload/PublicUploadPage').then((m) => ({ default: m.PublicUploadPage })),
);
const PublicSharePage = lazy(() =>
  import('@/components/share/PublicSharePage').then((m) => ({ default: m.PublicSharePage })),
);

function LazyFallback() {
  return (
    <div className="flex items-center justify-center h-32">
      <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LazyFallback />}>{children}</Suspense>;
}

// Stub for the future workspace-picker page (Phase 6 multi-membership).
function WorkspacesPickerStub() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold mb-2">Workspace picker</h1>
        <p className="text-muted-foreground">
          This page will let users with memberships in multiple workspaces switch between them.
          Coming in Phase 6.
        </p>
      </div>
    </div>
  );
}

// Legacy `/admin/accounts/:id` → canonical `/admin/workspaces/:id`.
function RedirectAccountToWorkspace() {
  const { id } = useParams();
  return <Navigate to={`/admin/workspaces/${id}`} replace />;
}

const adminRouter = createBrowserRouter([
  {
    path: '/upload/:token',
    element: (
      <Lazy>
        <PublicUploadPage />
      </Lazy>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/share/:token',
    element: (
      <Lazy>
        <PublicSharePage />
      </Lazy>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/admin" replace /> },
      { path: 'login', element: <LoginForm /> },
      { path: 'forgot-password', element: <ForgotPasswordForm /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'auth/callback', element: <AuthCallbackPage /> },
      {
        path: 'no-account',
        element: (
          <ProtectedRoute>
            <NoAccountPage />
          </ProtectedRoute>
        ),
      },
      {
        // Admin console shell — every child renders inside AdminConsoleLayout.
        path: 'admin',
        element: (
          <ProtectedRoute requiredRole="super_admin">
            <AdminConsoleLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <Lazy>
                <ICropDashboard />
              </Lazy>
            ),
          },
          {
            path: 'workspaces',
            element: (
              <Lazy>
                <WorkspacesList />
              </Lazy>
            ),
          },
          {
            path: 'workspaces/:id',
            element: (
              <Lazy>
                <WorkspaceDetail />
              </Lazy>
            ),
          },
          {
            path: 'users',
            element: (
              <Lazy>
                <UsersList />
              </Lazy>
            ),
          },
          {
            path: 'users/:id',
            element: (
              <Lazy>
                <UserDetail />
              </Lazy>
            ),
          },
          {
            path: 'plans',
            element: (
              <Lazy>
                <PlansList />
              </Lazy>
            ),
          },
          {
            path: 'plans/new',
            element: (
              <Lazy>
                <PlanForm />
              </Lazy>
            ),
          },
          {
            path: 'plans/:id',
            element: (
              <Lazy>
                <PlanForm />
              </Lazy>
            ),
          },
          {
            path: 'doctypes',
            element: (
              <Lazy>
                <GlobalDoctypesManager />
              </Lazy>
            ),
          },
          {
            path: 'org-types',
            element: (
              <Lazy>
                <GlobalOrgTypesManager />
              </Lazy>
            ),
          },
          {
            path: 'products',
            element: (
              <Lazy>
                <GlobalProductsManager />
              </Lazy>
            ),
          },
          {
            path: 'audit-log',
            element: (
              <Lazy>
                <AuditLogViewer />
              </Lazy>
            ),
          },
          { path: 'accounts/:id', element: <RedirectAccountToWorkspace /> },
        ],
      },
      {
        path: 'workspaces',
        element: (
          <ProtectedRoute>
            <WorkspacesPickerStub />
          </ProtectedRoute>
        ),
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

export function AdminRoutes() {
  return <RouterProvider router={adminRouter} />;
}
